import { jsPDF } from 'jspdf'
import { AXIS_SIZE } from 'live/services/charts/axis.styled'
import { pdfRenderProperties } from '../api/pdf/renderProperties'
import { addImage } from '../src/utils/addImage'
import { constWidgetSize } from '../src/utils/scale/constWidgetSize'
import { transformWidgets } from '../src/utils/widgets/transformWidgets'
import { PagePDF } from '../api/utils/types'
import { renderImagesDashboard } from '../src/utils/renderImagesDashboard'
import { createDivOutside } from '../src/utils/element/createDivOutside'
import { PageSizes, getWidthInchPage } from '../src/utils/getWidthInchPage'

const MAX_INCH_PER_PAGE = 180 // 20 inch free

interface ExportPdfPlotArea {
    span: string
    spanDepth: string
    jsonDashboard: any
    depthPerInch: number
    timePerInch: number
    filters: any
    pickerUnit: string
    pageSize: PageSizes
    loadingProcess?: (value: number) => void
}

export async function exportPdfPlotArea({
    depthPerInch,
    timePerInch,
    span,
    spanDepth,
    jsonDashboard,
    filters,
    pickerUnit,
    pageSize,
    loadingProcess = () => {}
}: ExportPdfPlotArea): Promise<{ preview: string; onSave: () => void }> {
    const [pageWidthInch, orientation] = getWidthInchPage(pageSize)
    const useCrop = true
    const elementRenderImages = createDivOutside()

    const { chartsRows, numberOfVisibleAxes } = transformWidgets(jsonDashboard)
    //TODO: Select the best row
    const charts = chartsRows[0]
    const renderProperties = await pdfRenderProperties({
        depthPerInch,
        span,
        spanDepth,
        timePerInch
    })
    const pages = renderProperties.crops.reduce((state, crop) => {
        const lastPage = state[state.length - 1] || {
            crops: [],
            ticks: 0
        }

        const cropHeight = Math.max(crop.ticksDepth, crop.ticksTime)
        if (lastPage.ticks == 0) {
            lastPage.crops.push(crop)
            lastPage.ticks = cropHeight
            state.push(lastPage)
        } else {
            if (lastPage.ticks + cropHeight > MAX_INCH_PER_PAGE) {
                state.push({
                    crops: [crop],
                    ticks: cropHeight
                })
            } else {
                lastPage.crops.push(crop)
                lastPage.ticks += cropHeight
            }
        }
        return state
    }, [] as PagePDF[])

    const chartHeader =
        AXIS_SIZE * numberOfVisibleAxes +
        (constWidgetSize.title - constWidgetSize.padding) +
        constWidgetSize.padding * 2 +
        constWidgetSize.fontHeight

    const paddingBottom =
        constWidgetSize.padding +
        constWidgetSize.footerPaddingBottom -
        constWidgetSize.fontHeight -
        constWidgetSize.border

    const [dashboardSize, imagesCrop] = await renderImagesDashboard({
        charts,
        cropItem: useCrop
            ? renderProperties.crops[0]
            : {
                  startSpan: renderProperties.rangeTime.start,
                  startSpanDepth: renderProperties.rangeDepth.start,
                  endSpanDepth: renderProperties.rangeDepth.end,
                  endSpan: renderProperties.rangeTime.end,
                  ticksDepth: renderProperties.rangeDepth.ticks,
                  ticksTime: renderProperties.rangeTime.ticks,
                  tickStartPosition: 0
              },
        widthIn: pageWidthInch,
        heightHeaderPx: chartHeader,
        heightFooterPx: paddingBottom,
        onLoadQueries() {},
        pdfRender: renderProperties,
        filters,
        pickerUnit
    })
    loadingProcess(1 / 2 / renderProperties.crops.length)
    const pageHeightInch = Math.max(
        Math.min(
            pages[0].ticks +
                parseInt(String(dashboardSize.header.in.height + dashboardSize.footer.in.height)) *
                    (useCrop ? pages[0].crops.length : 1) +
                5,
            MAX_INCH_PER_PAGE
        ),
        pageWidthInch
    )
    const doc = new jsPDF({
        unit: 'in',
        orientation: orientation,
        format: [pageWidthInch, pageHeightInch]
    })

    const imageHeaderData = imagesCrop[0]
    const imageBodyData = imagesCrop[1]
    const imageFooterData = imagesCrop[2]

    //INFO: Add header
    await addImage(
        doc,
        elementRenderImages,
        imageHeaderData,
        dashboardSize.header.px,
        dashboardSize.header.in,
        'header',
        orientation
    )

    await addImage(
        doc,
        elementRenderImages,
        imageBodyData,
        dashboardSize.body.px,
        dashboardSize.body.in,
        'part-1',
        orientation
    )

    await addImage(
        doc,
        elementRenderImages,
        imageFooterData,
        dashboardSize.footer.px,
        dashboardSize.footer.in,
        'footer',
        orientation
    )

    const previewPdf = doc.output('datauristring')

    loadingProcess(1 / renderProperties.crops.length)

    let previousY = dashboardSize.footer.in.y + dashboardSize.footer.in.height
    let cropsTotal = 1

    if (useCrop) {
        for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
            const currentPage = pages[pageIndex]

            if (pageIndex > 0) {
                previousY = 0
                doc.addPage(
                    [
                        pageWidthInch,
                        Math.max(
                            Math.min(
                                currentPage.ticks +
                                    parseInt(String(dashboardSize.header.in.height + dashboardSize.footer.in.height)) *
                                        currentPage.crops.length +
                                    5,
                                MAX_INCH_PER_PAGE
                            ),
                            pageWidthInch
                        )
                    ],
                    orientation
                )
                doc.setPage(pageIndex + 1)
            }

            for (let cropIndex = pageIndex == 0 ? 1 : 0; cropIndex < currentPage.crops.length; cropIndex++) {
                cropsTotal += 1
                const id = `${pageIndex}-${cropIndex}`
                const currentCropPart = currentPage.crops[cropIndex]

                const [dashboardPartSize, imagesDashboardPart] = await renderImagesDashboard({
                    charts,
                    cropItem: currentCropPart,
                    widthIn: pageWidthInch,
                    heightHeaderPx: chartHeader,
                    heightFooterPx: paddingBottom,
                    onLoadQueries() {},
                    pdfRender: renderProperties,
                    filters,
                    pickerUnit
                })
                loadingProcess(cropsTotal / 2 / renderProperties.crops.length)
                await addImage(
                    doc,
                    elementRenderImages,
                    imagesDashboardPart[0],
                    dashboardPartSize.header.px,
                    {
                        ...dashboardPartSize.header.in,
                        y: previousY
                    },
                    `header-${id}`,
                    orientation
                )

                await addImage(
                    doc,
                    elementRenderImages,
                    imagesDashboardPart[1],
                    dashboardPartSize.body.px,
                    {
                        ...dashboardPartSize.body.in,
                        y: previousY + dashboardPartSize.header.in.height
                    },
                    `part-${id}`,
                    orientation
                )

                await addImage(
                    doc,
                    elementRenderImages,
                    imagesDashboardPart[2],
                    dashboardPartSize.footer.px,
                    {
                        ...dashboardPartSize.footer.in,
                        y: previousY + dashboardPartSize.header.in.height + dashboardPartSize.body.in.height
                    },
                    `footer-${id}`,
                    orientation
                )
                loadingProcess(cropsTotal / renderProperties.crops.length)
                previousY =
                    previousY +
                    dashboardPartSize.header.in.height +
                    dashboardPartSize.body.in.height +
                    dashboardPartSize.footer.in.height

                if (cropIndex == currentPage.crops.length - 1) {
                    previousY = 0
                }
            }
        }
    }
    elementRenderImages.remove()
    return {
        preview: previewPdf,
        onSave: (fileName = 'file.pdf') => {
            doc.save(fileName)
        }
    }
}
